/** dd/mm/yyyy + 24-hour pickers that look the same in every browser/locale.
 *  Drop-in for <Input type="date|time|datetime-local">: same value strings
 *  ("YYYY-MM-DD", "HH:MM", "YYYY-MM-DDTHH:MM") and onChange({ target: { value } }). */
import { useState } from "react";
import { CalendarDays, X } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { cn } from "@/lib/utils";

const pad = (n) => String(n).padStart(2, "0");
const emit = (onChange, value) => onChange?.({ target: { value } });
const toYmd = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const fromYmd = (s) => { const [y, m, d] = (s || "").split("-").map(Number); return y ? new Date(y, m - 1, d) : undefined; };
export const toDmy = (s) => { const [y, m, d] = (s || "").split("-"); return y ? `${d}/${m}/${y}` : ""; };

const gcd = (a, b) => (b ? gcd(b, a % b) : a);
/** Minute step for booking times: bay > site slot length, refined by the turnaround buffer. */
export function minuteStepFor(site, bay) {
  const slot = bay?.slot_duration_minutes || site?.slot_duration_minutes || 30;
  const buffer = bay?.buffer_minutes ?? site?.default_buffer_minutes ?? 0;
  return Math.max(5, buffer > 0 ? gcd(slot, buffer) : slot);
}

const box = "h-9 rounded-md border border-input bg-transparent px-2 text-sm shadow-sm focus:outline-none focus:ring-1 focus:ring-ring disabled:cursor-not-allowed disabled:opacity-50";

export function AuDateInput({ value, onChange, min, max, disabled, className, id, required, "data-testid": testId }) {
  const [open, setOpen] = useState(false);
  const lo = fromYmd(min), hi = fromYmd(max);
  return (
    <div className={cn("flex items-center gap-1", className)}>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button type="button" id={id} disabled={disabled} data-testid={testId} data-value={value || ""}
                  className={cn(box, "flex w-full min-w-[8.5rem] items-center justify-between gap-2 text-left", !value && "text-slate-400")}>
            {value ? toDmy(value) : "dd/mm/yyyy"}
            <CalendarDays className="h-4 w-4 text-slate-400" />
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" align="start">
          <Calendar mode="single" weekStartsOn={1} selected={fromYmd(value)} defaultMonth={fromYmd(value) || lo}
                    disabled={[...(lo ? [{ before: lo }] : []), ...(hi ? [{ after: hi }] : [])]}
                    onSelect={(d) => { if (d) { emit(onChange, toYmd(d)); setOpen(false); } }} initialFocus />
        </PopoverContent>
      </Popover>
      {value && !required && !disabled && (
        <button type="button" onClick={() => emit(onChange, "")} className="text-slate-400 hover:text-slate-700"
                title="Clear date" data-testid={testId ? `${testId}-clear` : undefined}>
          <X className="h-3.5 w-3.5" />
        </button>
      )}
    </div>
  );
}

export function AuTimeInput({ value, onChange, min, max, minuteStep = 5, disabled, className, id, "data-testid": testId }) {
  const [h, m] = (value || "").split(":");
  const minH = min ? +min.slice(0, 2) : 0, maxH = max ? +max.slice(0, 2) : 23;
  const hours = Array.from({ length: 24 }, (_, i) => i).filter((i) => (i >= minH && i <= maxH) || pad(i) === h);
  const mins = Array.from({ length: Math.ceil(60 / minuteStep) }, (_, i) => pad(i * minuteStep))
    .filter((x) => !(min && h === min.slice(0, 2) && x < min.slice(3, 5)) && !(max && h === max.slice(0, 2) && x > max.slice(3, 5)));
  if (m && !mins.includes(m)) mins.push(m), mins.sort();
  const set = (hh, mm) => {
    if (hh === "") return emit(onChange, "");
    let m2 = mm || "00";
    if (max && hh === max.slice(0, 2) && m2 > max.slice(3, 5)) m2 = max.slice(3, 5);
    if (min && hh === min.slice(0, 2) && m2 < min.slice(3, 5)) m2 = min.slice(3, 5);
    return emit(onChange, `${hh}:${m2}`);
  };
  return (
    <div className={cn("flex items-center gap-1", className)} data-testid={testId} data-value={value || ""}>
      <select id={id} className={cn(box, "w-[4.25rem]")} value={h || ""} disabled={disabled} aria-label="Hour"
              onChange={(e) => set(e.target.value, m)} data-testid={testId ? `${testId}-hour` : undefined}>
        <option value="">--</option>
        {hours.map((i) => <option key={i} value={pad(i)}>{pad(i)}</option>)}
      </select>
      <span className="text-slate-400">:</span>
      <select className={cn(box, "w-[4.25rem]")} value={m || ""} disabled={disabled || !h} aria-label="Minute"
              onChange={(e) => set(h, e.target.value)} data-testid={testId ? `${testId}-minute` : undefined}>
        <option value="">--</option>
        {mins.map((x) => <option key={x} value={x}>{x}</option>)}
      </select>
    </div>
  );
}

export function AuDateTimeInput({ value, onChange, minuteStep = 5, disabled, className, "data-testid": testId }) {
  const [d, t] = (value || "").split("T");
  const join = (dd, tt) => emit(onChange, dd ? `${dd}T${(tt || "00:00").slice(0, 5)}` : "");
  return (
    <div className={cn("flex flex-wrap items-center gap-2", className)} data-testid={testId} data-value={value || ""}>
      <AuDateInput value={d || ""} disabled={disabled} onChange={(e) => join(e.target.value, t)}
                   data-testid={testId ? `${testId}-date` : undefined} />
      <AuTimeInput value={(t || "").slice(0, 5)} minuteStep={minuteStep} disabled={disabled || !d}
                   onChange={(e) => join(d, e.target.value)} data-testid={testId ? `${testId}-time` : undefined} />
    </div>
  );
}
